<template>
  <div class="q-pa-md">
    <!-- Заголовок и кнопка добавления -->
    <div class="row items-center q-mb-md">
      <div class="col">
        <h4 class="q-my-none">{{ t('facebook.accounts') }}</h4>
        <div class="text-caption text-grey-6">
          {{ t('facebook.total_accounts') }}: {{ pagination.rowsNumber || 0 }}
        </div>
      </div>
      <div class="col-auto">
        <q-btn
            color="primary"
            icon="add"
            :label="t('facebook.add_account')"
            @click="showAddDialog = true"
        />
      </div>
    </div>

    <!-- Информационная карточка для администратора -->
    <q-card v-if="isAdmin" class="q-mb-md bg-blue-1">
      <q-card-section>
        <div class="text-subtitle2 text-blue-8">
          <q-icon name="admin_panel_settings" class="q-mr-sm" />
          {{ t('facebook.admin_mode') }}
        </div>
        <div class="text-caption text-blue-7">
          {{ t('facebook.admin_mode_desc') }}
        </div>
      </q-card-section>
    </q-card>

    <!-- Фильтры -->
    <div class="row q-gutter-sm q-mb-md">
      <div class="col-12 col-sm-4">
        <q-input
            v-model="filters.search"
            outlined
            dense
            :placeholder="t('facebook.search_placeholder')"
            debounce="800"
            clearable
            @update:model-value="onFilterChange"
        >
          <template v-slot:prepend>
            <q-icon name="search" />
          </template>
        </q-input>
      </div>
      <div class="col-12 col-sm-3">
        <q-select
            v-model="filters.status"
            outlined
            dense
            :label="t('facebook.status')"
            :options="statusOptions"
            clearable
            emit-value
            map-options
            @update:model-value="onFilterChange"
        />
      </div>
    </div>

    <!-- Таблица с серверной пагинацией -->
    <q-table
        :rows="accounts"
        :columns="columns"
        row-key="id"
        :loading="loading"
        :pagination="pagination"
        v-model:pagination="pagination"
        @request="onRequest"
        flat
        bordered
        dense
        class="accounts-table"
        binary-state-sort
    >
      <!-- Расширяемые строки для отображения пикселей -->
      <template v-slot:body="props">
        <q-tr :props="props">
          <q-td
              v-for="col in props.cols"
              :key="col.name"
              :props="props"
          >
            <template v-if="col.name === 'account_name'">
              <div class="account-info">
                <div class="text-weight-medium">{{ col.value }}</div>
                <div class="text-caption text-grey-6">ID: {{ props.row.account_id }}</div>
              </div>
            </template>
            <template v-else-if="col.name === 'status'">
              <q-chip
                  :color="getStatusColor(col.value)"
                  text-color="white"
                  size="sm"
                  :label="getStatusLabel(col.value)"
              />
            </template>
            <template v-else-if="col.name === 'token_status'">
              <div class="token-status-container">
                <div class="token-circle-wrapper">
                  <q-circular-progress
                      :value="getTokenProgress(props.row)"
                      size="30px"
                      :thickness="0.2"
                      :color="getTokenStatusColor(props.row)"
                      track-color="grey-3"
                      class="q-ma-none"
                      show-value
                      font-size="8px"
                  >
                    {{ getTokenDaysRemaining(props.row) }}{{ t('facebook.days_short') }}
                  </q-circular-progress>
                </div>
              </div>
            </template>
            <template v-else-if="col.name === 'pixels'">
              <div class="pixels-info">
                <q-btn
                    :label="`${getPixelsCount(props.row.id)} ${t('facebook.pixels').toUpperCase()}`"
                    flat
                    dense
                    color="primary"
                    size="sm"
                    @click="togglePixels(props.row)"
                />
                <div v-if="props.row.last_pixel_sync_error" class="text-caption text-negative q-mt-xs">
                  {{ t('facebook.pixels_sync_error') }}
                </div>
              </div>
            </template>
            <template v-else-if="col.name === 'actions'">
              <div class="row no-wrap q-gutter-xs">
                <q-btn
                    flat
                    dense
                    round
                    icon="refresh"
                    color="primary"
                    @click="refreshToken(props.row)"
                    :loading="refreshingTokens[props.row.id]"
                >
                  <q-tooltip>{{ t('facebook.refresh_token') }}</q-tooltip>
                </q-btn>
                <q-btn
                    flat
                    dense
                    round
                    icon="sync"
                    color="info"
                    @click="syncPixels(props.row)"
                    :loading="syncingPixels[props.row.id]"
                >
                  <q-tooltip>{{ t('facebook.sync_pixels') }}</q-tooltip>
                </q-btn>
                <q-btn
                    flat
                    dense
                    round
                    icon="delete"
                    color="negative"
                    @click="confirmDeleteAccount(props.row)"
                >
                  <q-tooltip>{{ t('facebook.delete_account') }}</q-tooltip>
                </q-btn>
              </div>
            </template>
            <template v-else>
              {{ col.value }}
            </template>
          </q-td>
        </q-tr>

        <!-- Расширенная строка с пикселями -->
        <q-tr v-if="props.row.showPixels" :props="props" class="pixels-expanded">
          <q-td colspan="100%">
            <div class="q-pa-lg">
              <div class="text-subtitle1 text-weight-medium q-mb-md flex items-center">
                <q-icon name="settings_input_component" class="q-mr-sm" color="primary" />
                {{ t('facebook.account_pixels_for', { name: props.row.account_name }) }}
                <q-space />
                <q-btn
                    dense
                    flat
                    icon="sync"
                    color="primary"
                    @click="syncPixels(props.row)"
                    :loading="syncingPixels[props.row.id]"
                    :label="t('facebook.sync_pixels')"
                />
              </div>

              <div class="row q-gutter-md">
                <q-card
                    v-for="pixel in getAccountPixels(props.row.id)"
                    :key="pixel.pixel_id"
                    class="pixel-card-modern"
                    flat
                    bordered
                >
                  <!-- Шапка карточки с статусом -->
                  <div class="pixel-card-header" :class="pixel.status === 'active' ? 'bg-green-1' : 'bg-red-1'">
                    <div class="pixel-card-header-content">
                      <div class="pixel-title">
                        <q-icon name="settings_input_component" size="18px" class="q-mr-xs flex-shrink-0" :color="pixel.status === 'active' ? 'green-7' : 'red-7'" />
                        <span class="text-weight-medium pixel-name-text">
      {{ pixel.pixel_name || 'Facebook Pixel' }}
      <q-tooltip v-if="(pixel.pixel_name || 'Facebook Pixel').length > 20">
        {{ pixel.pixel_name || 'Facebook Pixel' }}
      </q-tooltip>
    </span>
                      </div>
                      <q-badge
                          :color="pixel.status === 'active' ? 'positive' : 'negative'"
                          :label="pixel.status === 'active' ? t('facebook.active') : t('facebook.inactive')"
                          outline
                          rounded
                          class="flex-shrink-0"
                      />
                    </div>
                  </div>

                  <q-card-section class="q-pa-md">
                    <!-- Основная информация -->
                    <div class="pixel-info-section q-mb-md">
                      <!-- ID пикселя -->
                      <div class="pixel-id-row q-mb-sm">
                        <q-icon name="fingerprint" size="16px" color="primary" class="q-mr-xs" />
                        <span class="text-weight-medium">{{ pixel.pixel_id }}</span>
                        <q-btn
                            flat
                            dense
                            round
                            icon="content_copy"
                            size="xs"
                            color="grey-6"
                            @click="copyToClipboard(pixel.pixel_id)"
                            class="q-ml-xs"
                        >
                          <q-tooltip>{{ t('common.copy') }}</q-tooltip>
                        </q-btn>
                      </div>

                      <!-- Источник пикселя -->
                      <div v-if="pixel.source_type" class="pixel-source-row q-mb-sm">
                        <q-icon
                            :name="pixel.source_type === 'business_manager' ? 'business' : 'campaign'"
                            size="14px"
                            color="blue-6"
                            class="q-mr-xs flex-shrink-0"
                        />
                        <div class="pixel-source-content">
                          <span class="text-caption text-grey-7">
                            {{ getSourceTypeLabel(pixel.source_type) }}:
                          </span>
                          <span class="text-caption text-weight-medium q-ml-xs">
                            {{ pixel.source_name || pixel.source_id }}
                            <q-tooltip v-if="(pixel.source_name || pixel.source_id).length > 25">
                              {{ pixel.source_name || pixel.source_id }}
                            </q-tooltip>
                          </span>
                        </div>
                      </div>
                    </div>

                    <!-- Временные метки -->
                    <div class="pixel-dates-section q-mb-md">
                      <div v-if="pixel.creation_time" class="date-row q-mb-xs">
                        <q-icon name="event_available" size="12px" color="green-6" class="q-mr-xs" />
                        <span class="text-caption text-grey-7">{{ t('facebook.created') }}:</span>
                        <span class="text-caption text-weight-medium q-ml-xs">{{ formatDate(pixel.creation_time) }}</span>
                      </div>

                      <div v-if="pixel.last_fired_time" class="date-row">
                        <q-icon name="flash_on" size="12px" color="orange-6" class="q-mr-xs" />
                        <span class="text-caption text-grey-7">{{ t('facebook.last_activity') }}:</span>
                        <span class="text-caption text-weight-medium q-ml-xs">{{ formatDate(pixel.last_fired_time) }}</span>
                      </div>

                      <div v-if="!pixel.last_fired_time" class="date-row">
                        <q-icon name="flash_off" size="12px" color="grey-5" class="q-mr-xs" />
                        <span class="text-caption text-grey-5">{{ t('facebook.no_activity') }}</span>
                      </div>
                    </div>

                    <!-- Действия -->
                    <div class="pixel-actions-section">
                      <q-btn-group spread flat class="full-width">
                        <q-btn
                            flat
                            dense
                            icon="send"
                            color="primary"
                            @click="openConversionDialog(pixel)"
                            size="sm"
                        >
                          <q-tooltip>{{ t('facebook.send_conversion') }}</q-tooltip>
                        </q-btn>
                        <q-btn
                            flat
                            dense
                            icon="link"
                            color="info"
                            @click="openUrlConstructorDialog(pixel)"
                            size="sm"
                        >
                          <q-tooltip>{{ t('url_constructor.tooltip') }}</q-tooltip>
                        </q-btn>
                        <q-btn
                            flat
                            dense
                            icon="delete_outline"
                            color="negative"
                            @click="confirmDeletePixel(pixel)"
                            size="sm"
                        >
                          <q-tooltip>{{ t('facebook.delete_pixel') }}</q-tooltip>
                        </q-btn>
                      </q-btn-group>
                    </div>
                  </q-card-section>
                </q-card>
              </div>

              <!-- Если пикселей нет -->
              <div v-if="getAccountPixels(props.row.id).length === 0" class="text-center q-pa-lg">
                <q-icon name="settings_input_component" size="48px" color="grey-4" />
                <div class="text-body1 text-grey-6 q-mt-md">
                  {{ t('facebook.no_pixels') }}
                </div>
                <div class="text-caption text-grey-5 q-mb-md">
                  {{ t('facebook.sync_pixels_desc') }}
                </div>
                <q-btn
                    color="primary"
                    icon="sync"
                    :label="t('facebook.sync_pixels')"
                    @click="syncPixels(props.row)"
                    :loading="syncingPixels[props.row.id]"
                />
              </div>
            </div>
          </q-td>
        </q-tr>
      </template>

      <!-- Загрузка -->
      <template v-slot:loading>
        <q-inner-loading showing color="primary" />
      </template>

      <!-- Пустое состояние -->
      <template v-slot:no-data>
        <div class="text-center q-pa-lg">
          <q-icon name="account_circle" size="64px" color="grey-5" />
          <div class="text-h6 q-mt-md text-grey-7">{{ t('facebook.no_accounts') }}</div>
          <div class="text-body2 text-grey-6">
            {{ hasActiveFilters ? t('facebook.no_accounts_filtered') : t('facebook.add_first_account') }}
          </div>
        </div>
      </template>

      <template v-slot:body-cell-owner="props">
        <q-td :props="props">
          <q-chip v-if="isAdmin && props.row.owner_username"
                  size="sm"
                  color="primary"
                  text-color="white">
            {{ props.row.owner_username }}
          </q-chip>
          <span v-else-if="!isAdmin">{{ t('facebook.my_account') }}</span>
          <span v-else>—</span>
        </q-td>
      </template>
    </q-table>

    <!-- Диалоги -->
    <q-dialog v-model="showAddDialog" persistent>
      <q-card style="min-width: 400px">
        <q-card-section>
          <div class="text-h6">{{ t('facebook.add_account') }}</div>
        </q-card-section>
        <q-card-section>
          <div class="q-mb-md">
            <q-btn
                color="primary"
                icon="fab fa-facebook"
                :label="t('facebook.login_facebook')"
                @click="loginWithFacebook"
                :loading="facebookLoading"
                class="full-width"
            />
          </div>
          <q-separator class="q-my-md" />
          <div class="text-body2 q-mb-sm">{{ t('facebook.enter_token_manually') }}:</div>
          <q-input
              v-model="manualToken"
              type="textarea"
              :label="t('facebook.access_token')"
              :placeholder="t('facebook.paste_token')"
              rows="3"
              outlined
          />
          <div v-if="addMessage" class="q-mt-md">
            <q-banner :class="addError ? 'bg-negative' : 'bg-positive'" text-color="white">
              {{ addMessage }}
            </q-banner>
          </div>
        </q-card-section>
        <q-card-actions align="right">
          <q-btn flat :label="t('common.cancel')" @click="closeAddDialog" />
          <q-btn
              color="primary"
              :label="t('common.add')"
              @click="addAccount"
              :loading="addingAccount"
              :disable="!manualToken.trim()"
          />
        </q-card-actions>
      </q-card>
    </q-dialog>

    <q-dialog v-model="showDeleteAccountDialog" persistent>
      <q-card>
        <q-card-section>
          <div class="text-h6">{{ t('facebook.delete_account') }}</div>
        </q-card-section>
        <q-card-section>
          {{ t('facebook.confirm_delete_account', { name: accountToDelete?.account_name }) }}
          <br><br>
          <small class="text-grey-6">{{ t('facebook.delete_account_warning') }}</small>
        </q-card-section>
        <q-card-actions align="right">
          <q-btn flat :label="t('common.cancel')" @click="showDeleteAccountDialog = false" />
          <q-btn
              color="negative"
              :label="t('common.delete')"
              @click="deleteAccount"
              :loading="deleting"
          />
        </q-card-actions>
      </q-card>
    </q-dialog>

    <q-dialog v-model="showDeletePixelDialog" persistent>
      <q-card>
        <q-card-section>
          <div class="text-h6">{{ t('facebook.delete_pixel') }}</div>
        </q-card-section>
        <q-card-section>
          {{ t('facebook.confirm_delete_pixel', { id: pixelToDelete?.pixel_id }) }}
          <br><br>
          <small class="text-grey-6">{{ t('facebook.delete_pixel_warning') }}</small>
        </q-card-section>
        <q-card-actions align="right">
          <q-btn flat :label="t('common.cancel')" @click="showDeletePixelDialog = false" />
          <q-btn
              color="negative"
              :label="t('common.delete')"
              @click="deletePixel"
              :loading="deletingPixel"
          />
        </q-card-actions>
      </q-card>
    </q-dialog>

    <!-- Диалог быстрой отправки конверсии -->
    <q-dialog v-model="showConversionDialog" persistent>
      <q-card style="min-width: 600px; max-width: 800px">
        <q-card-section>
          <div class="text-h6">
            {{ t('facebook.send_conversion') }}
            <span v-if="selectedPixelForConversion" class="text-caption text-grey-6">
              {{ t('facebook.for_pixel') }} {{ selectedPixelForConversion.pixel_id }}
            </span>
          </div>
        </q-card-section>
        <q-card-section class="q-pt-none">
          <QuickConversionForm
              v-if="selectedPixelForConversion"
              :preset-pixel-id="selectedPixelForConversion.pixel_id"
              :auto-fetch-pixels="false"
              @conversion-sent="onConversionSent"
          />
        </q-card-section>
        <q-card-actions align="right">
          <q-btn flat :label="t('common.close')" @click="closeConversionDialog" />
        </q-card-actions>
      </q-card>
    </q-dialog>

    <!-- Диалог конструктора URL -->
    <q-dialog v-model="showUrlConstructorDialog" persistent>
      <URLConstructorDialog
          :pixel="selectedPixelForUrl"
          @close="closeUrlConstructorDialog"
      />
    </q-dialog>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import { useQuasar } from 'quasar'
import { useI18n } from 'vue-i18n'
import api from '@/api/api'
import QuickConversionForm from '@/components/QuickConversionForm.vue'
import URLConstructorDialog from '@/components/URLConstructorDialog.vue'

import { useAuthStore } from '@/stores/auth'

const authStore = useAuthStore()
const isAdmin = computed(() => authStore.user?.role === 'admin')

const $q = useQuasar()
const { t } = useI18n()

// Данные
const accounts = ref([])
const pixels = ref([])
const loading = ref(false)
const showAddDialog = ref(false)
const showDeleteAccountDialog = ref(false)
const showDeletePixelDialog = ref(false)
const accountToDelete = ref(null)
const pixelToDelete = ref(null)
const deleting = ref(false)
const deletingPixel = ref(false)
const facebookLoading = ref(false)
const addingAccount = ref(false)
const manualToken = ref('')
const addMessage = ref('')
const addError = ref(false)
const refreshingTokens = ref({})
const syncingPixels = ref({})
const showConversionDialog = ref(false)
const selectedPixelForConversion = ref(null)

const showUrlConstructorDialog = ref(false)
const selectedPixelForUrl = ref(null)

// Фильтры
const filters = ref({
  search: '',
  status: ''
})

// Серверная пагинация
const pagination = ref({
  page: 1,
  rowsPerPage: 15,
  rowsNumber: 0,
  sortBy: 'created_at',
  descending: true
})

const statusOptions = computed(() => [
  { label: t('facebook.all_statuses'), value: '' },
  { label: t('facebook.active'), value: 'active' },
  { label: t('facebook.inactive'), value: 'inactive' },
  { label: t('facebook.error'), value: 'error' },
  { label: t('facebook.expired'), value: 'expired' }
])

const columns = computed(() => {
  const baseColumns = [
    { name: 'account_name', label: t('facebook.account'), field: 'account_name', align: 'left', sortable: true, style: 'width: 250px' },
    { name: 'status', label: t('facebook.status'), field: 'status', align: 'center', sortable: true, style: 'width: 120px' }
  ]

  // Добавляем колонку владельца для администратора
  if (isAdmin.value) {
    baseColumns.push({
      name: 'owner',
      label: t('facebook.owner'),
      field: 'owner_username',
      align: 'left',
      sortable: false,
      style: 'width: 150px'
    })
  }

  baseColumns.push(
      { name: 'token_status', label: t('facebook.token'), field: 'token_expires_at', align: 'center', sortable: true, style: 'width: 100px' },
      { name: 'pixels', label: t('facebook.pixels'), field: row => getPixelsCount(row.id), align: 'center', sortable: true, style: 'width: 120px' },
      { name: 'created_at', label: t('facebook.added_date'), field: 'created_at', align: 'center', sortable: true, format: val => formatDate(val), style: 'width: 120px' },
      { name: 'actions', label: t('facebook.actions'), field: '', align: 'center', style: 'width: 120px' }
  )

  return baseColumns
})

const hasActiveFilters = computed(() => !!(filters.value.search || filters.value.status))

// Серверная пагинация
const fetchAccounts = async (props = {}) => {
  loading.value = true
  try {
    const params = new URLSearchParams()
    const currentPagination = props.pagination || pagination.value

    params.append('limit', currentPagination.rowsPerPage)
    params.append('offset', (currentPagination.page - 1) * currentPagination.rowsPerPage)

    if (currentPagination.sortBy) {
      params.append('sort_by', currentPagination.sortBy)
      params.append('sort_order', currentPagination.descending ? 'desc' : 'asc')
    }

    if (filters.value.search) params.append('search', filters.value.search)
    if (filters.value.status) params.append('status', filters.value.status)

    const { data } = await api.get(`facebook-accounts?${params.toString()}`)

    accounts.value = data.accounts.map(account => ({ ...account, showPixels: false }))

    if (props.pagination) {
      pagination.value = { ...currentPagination, rowsNumber: data.total }
    } else {
      pagination.value.rowsNumber = data.total
    }

    await loadPixelsForCurrentAccounts()
  } catch (error) {
    $q.notify({ type: 'negative', message: t('notifications.accounts_load_error', { error: error.message }) })
  } finally {
    loading.value = false
  }
}

const onRequest = (props) => fetchAccounts(props)
const onFilterChange = () => { pagination.value.page = 1; fetchAccounts() }

// Методы пикселей
const loadPixelsForCurrentAccounts = async () => {
  try {
    pixels.value = []
    for (const account of accounts.value) {
      await loadPixelsForAccount(account.id)
    }
  } catch (error) {
    console.error('Ошибка загрузки пикселей:', error)
  }
}

const loadPixelsForAccount = async (accountId) => {
  try {
    const { data: response } = await api.get(`facebook-pixels?account_id=${accountId}`)
    // Получаем массив пикселей из ответа API
    const pixelsList = response.pixels || []
    // Удаляем старые пиксели для этого аккаунта
    pixels.value = pixels.value.filter(p => p.facebook_account_id !== accountId)
    // Добавляем новые пиксели
    pixels.value.push(...pixelsList.map(pixel => ({
      ...pixel,
      facebook_account_id: accountId
    })))
  } catch (error) {
    console.error(`Ошибка загрузки пикселей для аккаунта ${accountId}:`, error)
  }
}

const togglePixels = async (account) => {
  account.showPixels = !account.showPixels
  if (account.showPixels && !getAccountPixels(account.id).length) {
    await loadPixelsForAccount(account.id)
  }
}

// Utility функции
const getTokenDaysRemaining = (account) => {
  if (!account.token_expires_at) return 0
  const expiresAt = new Date(account.token_expires_at)
  const now = new Date()
  return Math.max(0, Math.ceil((expiresAt - now) / (1000 * 60 * 60 * 24)))
}

const getTokenProgress = (account) => {
  const daysRemaining = getTokenDaysRemaining(account)
  return Math.min(100, (daysRemaining / 60) * 100)
}

const getTokenStatusColor = (account) => {
  const days = getTokenDaysRemaining(account)
  if (days <= 0) return 'negative'
  if (days <= 7) return 'warning'
  return 'positive'
}

const getStatusColor = (status) => {
  const colors = { active: 'positive', inactive: 'warning', error: 'negative', expired: 'negative' }
  return colors[status] || 'grey'
}

const getStatusLabel = (status) => {
  const labels = {
    active: t('facebook.active'),
    inactive: t('facebook.inactive'),
    error: t('facebook.error'),
    expired: t('facebook.expired')
  }
  return labels[status] || t('common.unknown')
}

const formatDate = (dateStr) => {
  if (!dateStr) return '-'
  try {
    return new Date(dateStr).toLocaleDateString('ru-RU', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    })
  } catch (e) {
    return dateStr
  }
}

const getPixelsCount = (accountId) => pixels.value.filter(p => p.facebook_account_id === accountId).length
const getAccountPixels = (accountId) => pixels.value.filter(p => p.facebook_account_id === accountId)

// API методы
const refreshToken = async (account) => {
  refreshingTokens.value[account.id] = true
  try {
    await api.post('facebook-accounts', { action: 'refresh_token', account_id: account.id })
    $q.notify({
      type: 'positive',
      message: t('notifications.token_refreshed', { name: account.account_name }),
      position: 'top'
    })
    const index = accounts.value.findIndex(acc => acc.id === account.id)
    if (index !== -1) {
      const { data: updatedAccount } = await api.get(`facebook-accounts/${account.id}`)
      accounts.value[index] = { ...updatedAccount, showPixels: accounts.value[index].showPixels }
    }
  } catch (error) {
    $q.notify({
      type: 'negative',
      message: error.response?.data?.error || t('notifications.token_refresh_error'),
      position: 'top'
    })
  } finally {
    refreshingTokens.value[account.id] = false
  }
}

const syncPixels = async (account) => {
  syncingPixels.value[account.id] = true
  try {
    const { data } = await api.post('facebook-accounts', { action: 'sync_pixels', account_id: account.id })

    // Детальная информация о синхронизации
    const stats = data.stats
    let message = t('facebook.sync_completed') + ': '
    let details = []

    if (stats.added > 0) details.push(t('facebook.items_added', { count: stats.added }))
    if (stats.updated > 0) details.push(t('facebook.items_updated', { count: stats.updated }))
    if (stats.deactivated > 0) details.push(t('facebook.items_deactivated', { count: stats.deactivated }))

    message += details.length > 0 ? details.join(', ') : t('facebook.no_changes')
    message += ` (${t('facebook.total_in_facebook')}: ${stats.total})`

    $q.notify({
      type: 'positive',
      message: message,
      timeout: 5000,
      position: 'top'
    })

    // Если были изменения, показываем дополнительную информацию
    if (stats.added > 0 || stats.updated > 0 || stats.deactivated > 0) {
      $q.notify({
        type: 'info',
        message: t('facebook.pixels_synced_successfully', { name: account.account_name }),
        timeout: 3000,
        position: 'top'
      })
    }

    if (account)
      await loadPixelsForAccount(account.id)
    else
      await loadPixelsForCurrentAccounts()
  } catch (error) {
    $q.notify({
      type: 'negative',
      message: error.response?.data?.error || t('notifications.pixels_sync_error'),
      position: 'top'
    })
  } finally {
    syncingPixels.value[account.id] = false
  }
}

const loginWithFacebook = async () => {
  try {
    facebookLoading.value = true

    // Получаем конфигурацию Facebook
    const configResponse = await api.get('/config')
    const config = configResponse.data.facebook

    const authUrl = `https://www.facebook.com/${config.api_version}/dialog/oauth?` +
        `client_id=${config.app_id}&` +
        `redirect_uri=${encodeURIComponent(config.redirect_uri)}&` +
        `scope=${encodeURIComponent(config.scope)}&` +
        `response_type=code&` +
        `state=${Math.random().toString(36).substring(7)}`

    const popup = window.open(authUrl, 'facebook-auth',
        'width=600,height=800,scrollbars=yes,resizable=yes')

    const checkClosed = setInterval(() => {
      if (popup.closed) {
        clearInterval(checkClosed)
        facebookLoading.value = false
        window.removeEventListener('message', messageHandler)
      }
    }, 1000)

    const messageHandler = async (event) => {
      if (event.origin !== window.location.origin) return

      if (event.data.type === 'FACEBOOK_AUTH_SUCCESS') {
        clearInterval(checkClosed)
        popup.close()
        facebookLoading.value = false
        window.removeEventListener('message', messageHandler)

        await addAccountWithToken(event.data.accessToken)
      } else if (event.data.type === 'FACEBOOK_AUTH_ERROR') {
        clearInterval(checkClosed)
        popup.close()
        facebookLoading.value = false
        window.removeEventListener('message', messageHandler)

        $q.notify({
          type: 'negative',
          message: t('facebook.auth_error') + ': ' + event.data.error,
          position: 'top'
        })
      }
    }

    window.addEventListener('message', messageHandler)
  } catch (error) {
    console.error('Ошибка подключения Facebook:', error)
    facebookLoading.value = false
  }
}

const addAccountWithToken = async (accessToken) => {
  try {
    addingAccount.value = true
    addMessage.value = ''
    addError.value = false

    const response = await api.post('/facebook-accounts', {
      action: 'add_account',
      access_token: accessToken
    })

    $q.notify({
      type: 'positive',
      message: t('facebook.account_added_successfully'),
      position: 'top'
    })

    const accountData = { ...response.data, showPixels: false }

// Проверяем, обновляем ли существующий аккаунт или добавляем новый
    const existingIndex = accounts.value.findIndex(acc => acc.id === accountData.id)

    if (existingIndex !== -1) {
      // Обновляем существующий аккаунт
      accounts.value[existingIndex] = {
        ...accountData,
        showPixels: accounts.value[existingIndex].showPixels
      }
    } else {
      // Добавляем новый аккаунт
      accounts.value.unshift({
        ...accountData,
        showPixels: false,
        token_expires_at: accountData.token_expires_at || null,
        last_pixel_sync: accountData.last_pixel_sync || null,
        pixel_count: accountData.pixel_count || 0
      })
      pagination.value.rowsNumber += 1
    }

// Обновляем пиксели в общем массиве
    if (accountData.pixels && accountData.pixels.length > 0) {
      // Удаляем старые пиксели этого аккаунта
      pixels.value = pixels.value.filter(p => p.facebook_account_id !== accountData.id)

      // Добавляем новые пиксели
      const accountPixels = accountData.pixels.map(pixel => ({
        ...pixel,
        facebook_account_id: accountData.id
      }))
      pixels.value.push(...accountPixels)
    }
    /*if (newAccount.pixel_count > 0) {
      await syncPixels(newAccount)
    }*/
    closeAddDialog()
  } catch (error) {
    console.error('Ошибка добавления аккаунта:', error)
    const errorMessage = error.response?.data?.error || t('facebook.account_add_error')

    addError.value = true
    addMessage.value = errorMessage

    $q.notify({
      type: 'negative',
      message: errorMessage,
      position: 'top'
    })
  } finally {
    addingAccount.value = false
  }
}

const addAccount = async () => {
  if (!manualToken.value.trim()) {
    $q.notify({
      type: 'warning',
      message: t('facebook.enter_token_required'),
      position: 'top'
    })
    return
  }

  await addAccountWithToken(manualToken.value.trim())
}

const closeAddDialog = () => {
  showAddDialog.value = false
  manualToken.value = ''
  addMessage.value = ''
  addError.value = false
}

const confirmDeleteAccount = (account) => {
  accountToDelete.value = account
  showDeleteAccountDialog.value = true
}

const deleteAccount = async () => {
  if (!accountToDelete.value) return

  try {
    deleting.value = true

    const response = await api.delete(`/facebook-accounts?account_id=${accountToDelete.value.id}`)

    $q.notify({
      type: 'positive',
      message: response.data.message || t('facebook.account_deleted_successfully'),
      position: 'top'
    })

    // Показываем информацию об удаленных данных
    if (response.data.deleted_data) {
      const { conversions, pixels, stats } = response.data.deleted_data
      $q.notify({
        type: 'info',
        message: t('facebook.deleted_data_info', { conversions, pixels, stats }),
        position: 'top',
        timeout: 5000
      })
    }

    // ИСПРАВЛЕНИЕ: Сохраняем ID перед обнулением
    const deletedAccountId = accountToDelete.value.id

    showDeleteAccountDialog.value = false
    accountToDelete.value = null

    // Используем сохраненный ID
    const index = accounts.value.findIndex(acc => acc.id === deletedAccountId)
    if (index !== -1) {
      accounts.value.splice(index, 1)
      pagination.value.rowsNumber -= 1
    }

  } catch (error) {
    console.error('Ошибка удаления аккаунта:', error)
    const errorMessage = error.response?.data?.error || t('facebook.account_delete_error')
    $q.notify({
      type: 'negative',
      message: errorMessage,
      position: 'top'
    })
  } finally {
    deleting.value = false
  }
}

const confirmDeletePixel = (pixel) => {
  pixelToDelete.value = pixel
  showDeletePixelDialog.value = true
}

const deletePixel = async () => {
  if (!pixelToDelete.value) return
  deletingPixel.value = true
  try {
    await api.delete(`facebook-pixels?pixel_id=${pixelToDelete.value.pixel_id}&account_id=${pixelToDelete.value.facebook_account_id}`)
    $q.notify({
      type: 'positive',
      message: t('notifications.pixel_deleted', { id: pixelToDelete.value.pixel_id }),
      position: 'top'
    })
    showDeletePixelDialog.value = false
    await loadPixelsForCurrentAccounts()
  } catch (error) {
    $q.notify({
      type: 'negative',
      message: error.response?.data?.error || t('notifications.pixel_delete_error'),
      position: 'top'
    })
  } finally {
    deletingPixel.value = false
  }
}

const openConversionDialog = (pixel) => {
  selectedPixelForConversion.value = pixel
  showConversionDialog.value = true
}

const closeConversionDialog = () => {
  showConversionDialog.value = false
  selectedPixelForConversion.value = null
}

const onConversionSent = (data) => {
  $q.notify({
    type: 'positive',
    message: t('notifications.conversion_sent', { pixel_id: data.pixel_id }),
    caption: t('facebook.conversion_details', { id: data.conversion_id, score: data.quality_score })
  })
  closeConversionDialog()
}

// конструктор URL
const openUrlConstructorDialog = (pixel) => {
  selectedPixelForUrl.value = pixel
  showUrlConstructorDialog.value = true
}

const closeUrlConstructorDialog = () => {
  showUrlConstructorDialog.value = false
  selectedPixelForUrl.value = null
}

const copyToClipboard = async (text) => {
  try {
    await navigator.clipboard.writeText(text)
    $q.notify({
      type: 'positive',
      message: t('facebook.actions.copied_to_clipboard'),
      timeout: 1000
    })
  } catch (err) {
    console.error('Failed to copy: ', err)
  }
}

const getSourceTypeLabel = (sourceType) => {
  switch (sourceType) {
    case 'business_manager':
      return t('facebook.business_manager')
    case 'ad_account':
      return t('facebook.ad_account')
    default:
      return t('facebook.unknown_source')
  }
}

onMounted(() => fetchAccounts())
</script>

<style lang="scss" scoped>
.accounts-table {
  .account-info { min-width: 200px; }
  .token-status-container {
    min-width: 90px;
    .token-circle-wrapper { position: relative; }
  }
  .pixels-info { min-width: 100px; }
  .pixels-expanded {
    background-color: #f8f9fa;
  }
}

.pixel-card {
  min-width: 280px;
  max-width: 320px;
  position: relative;
  transition: all 0.3s ease;
  background: white;
  border-radius: 8px;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 24px rgba(0,0,0,0.12);
  }

  .pixel-delete-btn {
    opacity: 0.6;
    transition: opacity 0.2s ease;

    &:hover {
      opacity: 1;
    }
  }
}

.pixel-status-indicator {
  height: 4px;
  width: 100%;
  position: absolute;
  bottom: 0;
  left: 0;
  border-radius: 0 0 8px 8px;
}

.q-table__container .q-table {
  th { font-weight: 600; }
  td { padding: 8px 12px; }
}
.pixel-card-modern {
  width: 320px;
  min-height: 200px;
  transition: all 0.2s ease;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0,0,0,0.1);
  }
}

.pixel-card-header {
  padding: 12px 16px 8px 16px;
  border-bottom: 1px solid rgba(0,0,0,0.05);
}

.pixel-card-header-content {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.pixel-title {
  display: flex;
  align-items: center;
  font-size: 0.9rem;
}

.pixel-info-section,
.pixel-dates-section {
  border-bottom: 1px solid rgba(0,0,0,0.05);
  margin-bottom: 2px;
  padding-bottom: 2px;
}

.pixel-id-row,
.pixel-source-row,
.date-row {
  display: flex;
  align-items: center;
}

.pixel-actions-section .q-btn {
  font-size: 0.7rem;
  padding: 6px 8px;
}
.pixel-card-modern .q-card__section {
  padding: 8px 16px 12px 16px;
}

.pixel-info-section .pixel-id-row,
.pixel-info-section .pixel-source-row {
  margin-bottom: 4px;
}

.pixel-dates-section .date-row {
  margin-bottom: 2px;

  &:last-child {
    margin-bottom: 0;
  }
}
.pixel-source-row {
  display: flex;
  align-items: flex-start;
  min-width: 0; // Важно для корректной работы ellipsis
}

.pixel-source-content {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
}

.pixel-source-name {
  max-width: 180px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  display: inline-block;
}

.pixel-source-name:hover {
  overflow: visible;
  white-space: normal;
  word-break: break-all;
  position: relative;
  z-index: 1;
  background: white;
  padding: 2px 4px;
  border-radius: 4px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.1);
}

.pixel-card-header-content {
  display: flex;
  justify-content: space-between;
  align-items: center;
  min-width: 0; // Важно для ellipsis
}

.pixel-title {
  display: flex;
  align-items: center;
  font-size: 0.9rem;
  flex: 1;
  min-width: 0; // Важно для ellipsis
}

.pixel-name-text {
  max-width: 200px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  display: inline-block;
}

.pixel-source-row {
  display: flex;
  align-items: flex-start;
  min-width: 0;
}

.pixel-source-content {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
}

.pixel-source-name {
  max-width: 180px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  display: inline-block;
}
</style>